import { DAY_MS, getEnvelopeLifecycle, getLegacyPoliceReport } from '../../../../functions/envelopes/envelopeLifecycle.mjs'
export { HOLD_DAYS, DAY_MS, holdProgress, isEnvelopeReady, daysUntilReady, isAwaitingReport } from '../../../../functions/envelopes/envelopeLifecycle.mjs'

/**
 * gold_transactions live in TWO shapes:
 *  - migrated (shop app):  totalPayout, totalWeightGrams, desagioPercent,
 *    goldPrice24k, holdUntil, items:{karat, weightGrams, purityPercent,
 *    pricePerGram, subtotal}
 *  - portal (TransactionFlow): totals:{totalWeight,totalPureWeight,totalGross,
 *    totalDesagio,totalNet}, prices:{gold,silver}, items:{metal, karatWhole,
 *    karatDecimal, weight, itemType}
 * Everything analytical (dashboard, envelopes, profit report) reads through
 * this normalizer so both shapes behave identically.
 */

// Deságio buckets (mirrors the calculator; used only to split gold vs silver
// payout on portal-shape docs that mix metals)
const COIN_BAR_TYPES = new Set(['coin', 'bar'])
const DESAGIO_JEWELRY = 30
const DESAGIO_COIN_BAR = 20

export function toDate(value) {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export function normalizeTx(id, raw) {
  const legacyPoliceReport = getLegacyPoliceReport(raw)
  const createdAt = legacyPoliceReport ? new Date(legacyPoliceReport.purchaseAt.getTime()) : toDate(raw.createdAt)
  // A purchase date or historical holdUntil alone is not reporting evidence.
  // New reports use their receipt; validated historical police/email reporting
  // uses its separately recorded purchase-date hold basis.
  const lifecycle = getEnvelopeLifecycle(raw)

  const goldPriceAtPurchase = Number(raw.goldPrice24k ?? raw.prices?.gold) || null

  let goldGrams = 0
  let goldPureGrams = 0
  let silverGrams = 0
  let goldPaidFromItems = 0
  let goldDesagioFromItems = 0
  let sawItemPayouts = false
  let sawItemDesagio = false

  for (const it of raw.items || []) {
    if (it.weightGrams != null || it.karat != null) {
      // migrated shape (karat 0 = silver)
      const w = Number(it.weightGrams) || 0
      const karat = Number(it.karat) || 0
      if (karat === 0) {
        silverGrams += w
      } else {
        goldGrams += w
        const purity = it.purityPercent != null ? Number(it.purityPercent) / 100 : karat / 24
        const pure = w * purity
        goldPureGrams += pure
        if (it.subtotal != null) {
          goldPaidFromItems += Number(it.subtotal) || 0
          sawItemPayouts = true
          if (goldPriceAtPurchase) {
            goldDesagioFromItems += Math.max(0, pure * goldPriceAtPurchase - (Number(it.subtotal) || 0))
            sawItemDesagio = true
          }
        }
      }
    } else {
      // portal shape
      const w = Number(it.weight) || 0
      if (it.metal === 'silver') {
        silverGrams += w
      } else {
        goldGrams += w
        const karat = (parseFloat(it.karatWhole) || 18) + (parseFloat(it.karatDecimal) || 0) / 100
        const pure = w * (karat / 24)
        goldPureGrams += pure
        if (goldPriceAtPurchase) {
          const rate = COIN_BAR_TYPES.has(it.itemType) ? DESAGIO_COIN_BAR : DESAGIO_JEWELRY
          goldPaidFromItems += pure * goldPriceAtPurchase * (1 - rate / 100)
          goldDesagioFromItems += pure * goldPriceAtPurchase * (rate / 100)
          sawItemPayouts = true
          sawItemDesagio = true
        }
      }
    }
  }

  const payout = Number(raw.totalPayout ?? raw.totals?.totalNet) || 0
  // AU-only spend: exact stored payout when the transaction is gold-only
  // (the common case); per-item reconstruction only when silver is mixed in.
  const goldPaid = silverGrams === 0 ? payout : sawItemPayouts ? goldPaidFromItems : payout

  const weightGrams = Number(raw.totalWeightGrams ?? raw.totals?.totalWeight) || goldGrams + silverGrams

  const desagioPercent =
    raw.desagioPercent != null
      ? Number(raw.desagioPercent)
      : raw.totals?.totalGross
        ? (Number(raw.totals.totalDesagio) / Number(raw.totals.totalGross)) * 100
        : null

  // Deságio captured in dollars — AU ONLY (the profit report excludes silver).
  // Gold-only portal docs: the stored aggregate is exact. Anything mixing
  // silver (either shape): rebuild from gold items so silver deságio never
  // contaminates the number and migrated mixed docs don't drop to null.
  const desagioAmount =
    silverGrams === 0 && raw.totals?.totalDesagio != null
      ? Number(raw.totals.totalDesagio)
      : sawItemDesagio
        ? goldDesagioFromItems
        : silverGrams === 0 && goldPriceAtPurchase
          ? Math.max(0, goldPureGrams * goldPriceAtPurchase - payout)
          : null

  return {
    id,
    transactionNumber: raw.transactionNumber || id,
    envelopeNumber: raw.envelopeNumber || '',
    customerName: raw.customer?.name || `${raw.customer?.firstName || ''} ${raw.customer?.lastName || ''}`.trim(),
    operatorName: raw.operator?.name || '',
    status: raw.status || 'completed',
    // Cancelled sales (seller withdrew): record is kept for legal retention,
    // but the envelope/stats treat it as void.
    cancelledAt: toDate(raw.cancelledAt),
    cancelledBy: raw.cancelledBy?.name || null,
    cancelReason: raw.cancelReason || null,
    createdAt,
    reportConfirmation: raw.reportConfirmation || null,
    reportDelivery: raw.reportDelivery || null,
    // Pass only validated metadata. Normalizing a malformed raw timestamp must
    // never promote an invalid migration into a valid historical report.
    ...(legacyPoliceReport ? { legacyPoliceReport } : {}),
    ...lifecycle,
    claimedAt: toDate(raw.envelopeClaimedAt),
    claimedBy: raw.envelopeClaimedBy || null,
    payout,
    weightGrams,
    goldGrams,
    goldPureGrams,
    silverGrams,
    goldPriceAtPurchase,
    goldPaid,
    desagioPercent,
    desagioAmount,
  }
}

export function inLastDays(tx, days, now = new Date()) {
  return !!tx.createdAt && now.getTime() - tx.createdAt.getTime() <= days * DAY_MS
}

export const isCancelledTx = (tx) => tx.status === 'cancelled'

export const usd = (v) => {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n)
    ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—'
}

export const grams = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })} g` : '—'
}
